import { useMemo } from 'react';
import { menuPaths, type FabricaMenu } from '@/auth/fabricaMenus';
import { navigationForPortal, type NavSection } from '@/config/navigation';
import { isMenuPageWithheld, navigationFromFabrica } from '@/config/fabricaNavigation';
import { portalOfPath, type Portal, type RoleCode } from '@/routes/roles';
import { useGlobalSelector } from './useGlobalState';
import { useRole } from './useRole';

/**
 * The Fabrica menu tree of the role `roleCode` for the signed-in person, or null when menus are
 * not loaded. With menus loaded, a code the person does not hold has no menus.
 */
export function useFabricaMenus(roleCode: RoleCode): FabricaMenu[] | null {
  const menus = useGlobalSelector((s) => s.menus);
  const roles = useGlobalSelector((s) => s.access?.roles);
  return useMemo(() => {
    if (!menus) return null;
    const roleId = roles?.find((r) => r.code === roleCode)?.roleId;
    return roleId !== undefined ? (menus[roleId] ?? []) : [];
  }, [menus, roles, roleCode]);
}

/**
 * The sidebar sections of the role `roleCode` in `portal`: only Fabrica's menus once they are loaded
 * (DebtCollectify's GetMenuListByRoles); the portal's built-in ones in sample data.
 */
export function useNavigation(portal: Portal, roleCode: RoleCode): NavSection[] {
  const menus = useFabricaMenus(roleCode);
  return useMemo(() => (menus ? navigationFromFabrica(portal, menus) : navigationForPortal(portal)), [menus, portal]);
}

/** Every page path the role's Fabrica menus lead to, or null when menus are not loaded. */
export function useFabricaMenuPaths(roleCode: RoleCode): string[] | null {
  const menus = useFabricaMenus(roleCode);
  return useMemo(() => (menus ? menuPaths(menus) : null), [menus]);
}

/**
 * True when the active role can open the page at `path` (query allowed): its portal is one the role
 * reaches and, once Fabrica's menus are loaded, the role has a menu over it (see `MenuAccessGuard`).
 * For links and tabs that lead to another page; a page's own tabs follow the page's menu.
 */
export function useCanOpen(path: string): boolean {
  return useCanOpenAny()(path);
}

/** As `useCanOpen`, for a list of links: a function answering it for any path. */
export function useCanOpenAny(): (path: string) => boolean {
  const { portals, roleCode } = useRole();
  const paths = useFabricaMenuPaths(roleCode);
  return useMemo(
    () => (path: string) => {
      const pathname = path.split(/[?#]/)[0] ?? path;
      const portal = portalOfPath(pathname);
      if (portal && !portals.includes(portal)) return false;
      return !paths || !isMenuPageWithheld(paths, pathname);
    },
    [portals, paths],
  );
}
